import React, { useState, useEffect, useRef } from "react";
import {
  Drawer,
  Descriptions,
  Tag,
  Divider,
  Card,
  Row,
  Col,
  Statistic,
  Spin,
  Alert,
  Button,
  Modal,
  Form,
  Input,
  DatePicker,
  Select,
} from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  MailOutlined,
  HomeOutlined,
  CarOutlined,
  TeamOutlined,
  AlertOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
} from "@ant-design/icons";
import Utilities from "../../../dependencies/custom/react-utilities";
import Settings from "../../../dependencies/custom/settings";
import ValuesStore from "../../../store/values-store";
import CustomFunctions from "../../../dependencies/custom/custom-functions";
import dayjs from "dayjs";

/**
 * Student Detail Drawer
 * Shows comprehensive student information including:
 * - Personal details
 * - Academic info
 * - Parent/Guardian info
 * - Emergency contact
 * - Transport info
 * - Financial summary
 */
const StudentDetailDrawer = ({
  visible,
  onClose,
  student,
  loading = false,
  onUpdated,
}) => {
  const valuesStore = ValuesStore();
  const myRoles =
    valuesStore.getValue("myRoles") ||
    JSON.parse(sessionStorage.getItem("myRoles") || "[]");
  const nationalityOptions = Array.isArray(valuesStore.getValue("nationality"))
    ? valuesStore
        .getValue("nationality")
        .map((item) => ({ value: item.nationality, label: item.nationality }))
    : [];
  const relationshipOptions = Array.isArray(
    valuesStore.getValue("relationship"),
  )
    ? valuesStore
        .getValue("relationship")
        .map((item) => ({ value: item.id, label: item.name }))
    : [];
  const isSystemAdmin =
    Array.isArray(myRoles) && myRoles.includes("System Administrator");

  const [editSection, setEditSection] = useState(null);
  const [editLoading, setEditLoading] = useState(false);
  const [localStudent, setLocalStudent] = useState(student);
  const [terms, setTerms] = useState([]);
  const [accommodations, setAccommodations] = useState([]);
  const [contextOptionsLoading, setContextOptionsLoading] = useState(false);
  const [contextOptionsError, setContextOptionsError] = useState("");
  const [contextOptionsVersion, setContextOptionsVersion] = useState(0);
  const [academicError, setAcademicError] = useState("");
  const academicSaving = useRef(false);
  const [editForm] = Form.useForm();
  const editDayBoarding = Form.useWatch("dayBoarding", editForm);

  useEffect(() => {
    setLocalStudent(student);
  }, [student]);

  useEffect(() => {
    if (!visible || !student?.id) return undefined;
    let current = true;
    Utilities.fetchWithAuth(
      "GET",
      `${Settings.urls.enrollment.student}/${student.id}`,
    ).then((response) => {
      if (current && response?.ok) {
        setLocalStudent((previous) =>
          previous?.id === student.id
            ? { ...previous, linField: response.data?.linField }
            : previous,
        );
      }
    });
    return () => {
      current = false;
    };
  }, [visible, student?.id]);

  useEffect(() => {
    if (!visible || !isSystemAdmin || editSection !== "academic") return;
    let current = true;
    setContextOptionsLoading(true);
    setContextOptionsError("");

    async function fetchContextOptions() {
      try {
        const responses = await Promise.all([
          Utilities.fetchWithAuth("GET", Settings.urls.enrollment.terms),
          Utilities.fetchWithAuth(
            "GET",
            `${Settings.urls.enrollment.accommodation}?category=House`,
          ),
          Utilities.fetchWithAuth(
            "GET",
            `${Settings.urls.enrollment.accommodation}?category=Dormitory`,
          ),
        ]);
        if (!current) return;
        const failed = responses.find((response) => !response?.ok);
        if (failed) {
          setContextOptionsError(
            Utilities.formatErrorMessage(
              { ...failed, msg: failed.data ? undefined : failed.msg },
              "Unable to load academic options.",
            ),
          );
          return;
        }
        const asList = (response) =>
          Array.isArray(response.data)
            ? response.data
            : response.data?.results || [];
        setTerms(asList(responses[0]));
        setAccommodations([...asList(responses[1]), ...asList(responses[2])]);
      } catch (error) {
        if (current)
          setContextOptionsError(
            error.message || "Unable to load academic options.",
          );
      } finally {
        if (current) setContextOptionsLoading(false);
      }
    }

    fetchContextOptions();
    return () => {
      current = false;
    };
  }, [visible, isSystemAdmin, editSection, contextOptionsVersion]);

  async function openEdit(section) {
    editForm.resetFields();
    if (section === "student") {
      const response = await Utilities.fetchWithAuth(
        "GET",
        `${Settings.urls.enrollment.student}/${localStudent?.id}`,
      );
      if (!response?.ok) {
        Utilities.showNotification(
          undefined,
          response?.msg || "Unable to load student record",
        );
        return;
      }
      editForm.setFieldsValue({
        firstName: localStudent?.firstName,
        lastName: localStudent?.lastName,
        middleName: localStudent?.middleName,
        dob: localStudent?.dob ? dayjs(localStudent.dob) : null,
        gender: localStudent?.gender,
        nationality: localStudent?.nationality,
        linField: response.data?.linField || "",
      });
    }
    if (section === "academic") {
      setAcademicError("");
      setContextOptionsLoading(true);
      editForm.setFieldsValue({
        dayBoarding: localStudent?.dayBoarding || "Day",
        currentTermId: localStudent?.currentTermId,
        houseID: localStudent?.boarding?.house?.id,
        dormitoryID: localStudent?.boarding?.dormitory?.id,
      });
    }
    if (section === "parent")
      editForm.setFieldsValue({
        parentName: localStudent?.parent?.fullName,
        email: localStudent?.parent?.email,
        relationshipId: localStudent?.parent?.relationshipId,
        phonePrimary: localStudent?.parent?.phone,
        phoneAlt: localStudent?.parent?.phoneAlt,
        address: localStudent?.parent?.address,
        occupation: localStudent?.parent?.occupation,
        countryCodePri: localStudent?.parent?.countryCodePri || "+256",
        countryCodeAlt: localStudent?.parent?.countryCodeAlt || "+256",
      });
    if (section === "emergency")
      editForm.setFieldsValue({
        emgName: localStudent?.emergencyContact?.name,
        emgRelId: localStudent?.emergencyContact?.relationshipId,
        emgPhone: localStudent?.emergencyContact?.phone,
        countryCodeEmg: localStudent?.emergencyContact?.countryCode || "+256",
      });
    setEditSection(section);
  }

  async function saveEdit() {
    if (editLoading || academicSaving.current) return;
    const values = await editForm.validateFields();
    if (editSection === "academic") {
      await saveAcademicEdit(values);
      return;
    }
    if (values.dob) values.dob = dayjs(values.dob).format("YYYY-MM-DD");
    setEditLoading(true);
    const endpointMap = {
      student: `${Settings.urls.enrollment.student}/${localStudent?.id}`,
      parent: `${Settings.urls.enrollment.guardian}/${localStudent?.parent?.id}`,
      emergency: `${Settings.urls.enrollment.emergency}/${localStudent?.emergencyContact?.id}`,
    };
    const res = await Utilities.fetchWithAuth(
      "PATCH",
      endpointMap[editSection],
      values,
    );
    if (res?.ok) {
      setLocalStudent((prev) => {
        if (editSection === "student")
          return {
            ...prev,
            ...values,
            fullName:
              `${values.firstName || ""} ${values.middleName || ""} ${values.lastName || ""}`.trim(),
          };
        if (editSection === "parent")
          return {
            ...prev,
            parent: {
              ...prev.parent,
              fullName: values.parentName,
              email: values.email,
              phone: values.phonePrimary,
              phoneAlt: values.phoneAlt,
              address: values.address,
              occupation: values.occupation,
            },
          };
        if (editSection === "emergency")
          return {
            ...prev,
            emergencyContact: {
              ...prev.emergencyContact,
              name: values.emgName,
              phone: values.emgPhone,
              countryCode: values.countryCodeEmg,
            },
          };
        return prev;
      });
      Utilities.showNotification(
        undefined,
        "Updated successfully",
        "text-success",
      );
      setEditSection(null);
    } else {
      Utilities.showNotification(undefined, res?.msg || "Update failed");
    }
    setEditLoading(false);
  }

  async function saveAcademicEdit(values) {
    if (contextOptionsLoading || contextOptionsError || !isSystemAdmin) return;
    academicSaving.current = true;
    setEditLoading(true);
    setAcademicError("");
    let savedAnything = false;
    let complete = false;
    let reconciliation;
    try {
      const boarding = values.dayBoarding === "Boarding";
      const currentHouse = localStudent?.boarding?.house;
      const currentDormitory = localStudent?.boarding?.dormitory;
      const houseChanged =
        values.houseID != null &&
        String(values.houseID) !== String(currentHouse?.id);
      const dormitoryChanged =
        boarding &&
        values.dormitoryID != null &&
        String(values.dormitoryID) !== String(currentDormitory?.id);
      const contextChanged =
        values.dayBoarding !== localStudent?.dayBoarding ||
        String(values.currentTermId) !== String(localStudent?.currentTermId) ||
        dormitoryChanged;

      for (const [category, id, changed] of [
        ["House", values.houseID, houseChanged],
        ["Dormitory", values.dormitoryID, dormitoryChanged],
      ]) {
        if (!changed) continue;
        const venue = accommodations.find(
          (item) =>
            item.category === category && String(item.id) === String(id),
        );
        if (
          !venue ||
          (category === "Dormitory" && Number(venue.remainingSpace) <= 0)
        ) {
          setAcademicError(`Select an available ${category.toLowerCase()}.`);
          return;
        }
      }

      if (contextChanged) {
        const payload = {
          dayBoarding: values.dayBoarding,
          currentTermId: values.currentTermId,
        };
        if (dormitoryChanged) payload.accomodationID = values.dormitoryID;
        const response = await Utilities.fetchWithAuth(
          "PATCH",
          `${Settings.urls.enrollment.student}/${localStudent.id}/status-context`,
          payload,
        );
        if (!response?.ok) {
          setAcademicError(
            Utilities.formatErrorMessage(
              { ...response, msg: response?.data ? undefined : response?.msg },
              "Unable to update academic context.",
            ),
          );
          return;
        }
        savedAnything = true;
        reconciliation = response.data?.reconciliation;
        const dormitory = !boarding
          ? null
          : dormitoryChanged
            ? {
                id: values.dormitoryID,
                name: accommodations.find(
                  (item) => String(item.id) === String(values.dormitoryID),
                )?.name,
              }
            : currentDormitory;
        setLocalStudent((previous) => ({
          ...previous,
          dayBoarding: values.dayBoarding,
          currentTermId: values.currentTermId,
          currentTerm:
            terms.find(
              (term) => String(term.id) === String(values.currentTermId),
            )?.name || previous.currentTerm,
          boarding: {
            house: currentHouse || null,
            dormitory,
            status: currentHouse || dormitory ? "Assigned" : "Unassigned",
          },
        }));
      }

      if (houseChanged) {
        const success = await CustomFunctions.submitAccommodation(
          "POST",
          localStudent,
          values.houseID,
          localStudent.id,
        );
        if (!success) {
          setAcademicError(
            `${savedAnything ? "Academic context saved. " : ""}House could not be saved. Review the selection and retry.`,
          );
          return;
        }
        savedAnything = true;
        const house = accommodations.find(
          (item) => String(item.id) === String(values.houseID),
        );
        setLocalStudent((previous) => ({
          ...previous,
          boarding: {
            ...previous.boarding,
            house: { id: house.id, name: house.name },
            status: "Assigned",
          },
        }));
      }
      complete = true;
    } catch (error) {
      setAcademicError(
        `${savedAnything ? "Some changes were saved. " : ""}${error.message || "Unable to save academic context. Please retry."}`,
      );
    } finally {
      if (savedAnything) {
        try {
          const response = await Utilities.fetchWithAuth(
            "GET",
            Settings.urls.academics.studentSearchDetail.replace(
              ":identifier",
              encodeURIComponent(localStudent.id),
            ),
          );
          if (response?.ok) {
            setLocalStudent((previous) => ({ ...previous, ...response.data }));
          } else {
            Utilities.showNotification(
              undefined,
              "Changes saved, but student details could not be refreshed. Reopen the record to reload.",
            );
          }
          await onUpdated?.();
        } catch {
          Utilities.showNotification(
            undefined,
            "Changes saved, but student details could not be refreshed. Reopen the record to reload.",
          );
        }
      }
      setContextOptionsVersion((version) => version + 1);
      if (complete) {
        Utilities.showNotification(
          "Academic context saved",
          reconciliation
            ? `${reconciliation.feesAssigned || 0} fee(s) assigned, ${reconciliation.unpaidFeesRemoved || 0} unpaid fee(s) removed, ${reconciliation.transportBookingsRemoved || 0} transport booking(s) removed.`
            : "Accommodation updated successfully.",
          "text-success",
        );
        setEditSection(null);
      }
      academicSaving.current = false;
      setEditLoading(false);
    }
  }

  if (!student && !loading) {
    return null;
  }

  const formatCurrency = (value) => {
    const num = parseFloat(value) || 0;
    return `UGX ${num.toLocaleString()}`;
  };

  const transportBookings = Array.isArray(localStudent?.transport)
    ? localStudent.transport
    : [];
  const hasTransport = transportBookings.length > 0;
  const latestTransport = hasTransport ? transportBookings[0] : null;

  return (
    <Drawer
      title={
        <span>
          <UserOutlined style={{ marginRight: 8 }} />
          Student Details
        </span>
      }
      placement="right"
      onClose={onClose}
      open={visible}
      width={900}
      bodyStyle={{ padding: "16px" }}
    >
      {loading ? (
        <div style={{ textAlign: "center", padding: "50px" }}>
          <Spin size="large" />
          <p style={{ marginTop: 16, color: "#888" }}>
            Loading student details...
          </p>
        </div>
      ) : student ? (
        <>
          {/* Personal Information */}
          <Card
            size="small"
            title={
              <span
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  <UserOutlined style={{ marginRight: 8 }} />
                  Personal Information
                </span>
                {isSystemAdmin && (
                  <Button
                    size="small"
                    icon={<i className="fas fa-edit" />}
                    onClick={() => openEdit("student")}
                  >
                    Edit
                  </Button>
                )}
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={3} size="small" bordered>
              <Descriptions.Item label="First Name">
                <strong>{localStudent?.firstName || "-"}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Last Name">
                <strong>{localStudent?.lastName || "-"}</strong>
              </Descriptions.Item>
              <Descriptions.Item label="Middle Name">
                {localStudent?.middleName || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="SPC Code">
                {localStudent?.spc || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Application Code">
                {localStudent?.applicationCode || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Date of Birth">
                {localStudent?.dob || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Gender">
                {localStudent?.gender || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Nationality">
                {localStudent?.nationality || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="LIN">
                {localStudent?.linField || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Status">
                <Tag color="success">{localStudent?.admissionStatus}</Tag>
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Academic Information */}
          <Card
            size="small"
            title={
              <span
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  <TeamOutlined style={{ marginRight: 8 }} />
                  Academic Information
                </span>
                {isSystemAdmin && (
                  <Button
                    size="small"
                    icon={<i className="fas fa-edit" />}
                    onClick={() => openEdit("academic")}
                  >
                    Edit
                  </Button>
                )}
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Class">
                {localStudent?.class?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Stream">
                {localStudent?.stream?.name || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Day/Boarding">
                <Tag
                  color={
                    localStudent?.dayBoarding === "Boarding" ? "purple" : "cyan"
                  }
                >
                  {localStudent?.dayBoarding}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="Current Term">
                {localStudent?.currentTerm || "-"}
              </Descriptions.Item>
              <Descriptions.Item label="Academic Year" span={2}>
                {localStudent?.currentAcadyr || "-"}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          <Card
            size="small"
            title={
              <span>
                <HomeOutlined style={{ marginRight: 8 }} />
                Accommodation
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            <Descriptions column={2} size="small" bordered>
              <Descriptions.Item label="Accommodation Status" span={2}>
                <Tag
                  color={
                    localStudent?.boarding?.status === "Assigned"
                      ? "green"
                      : "warning"
                  }
                >
                  {localStudent?.boarding?.status || "Unassigned"}
                </Tag>
              </Descriptions.Item>
              <Descriptions.Item label="House">
                {localStudent?.boarding?.house?.name || "Unassigned"}
              </Descriptions.Item>
              <Descriptions.Item label="Dormitory">
                {localStudent?.boarding?.dormitory?.name ||
                  (localStudent?.dayBoarding === "Boarding"
                    ? "Unassigned"
                    : "Not applicable")}
              </Descriptions.Item>
            </Descriptions>
          </Card>

          {/* Parent/Guardian Information */}
          <Card
            size="small"
            title={
              <span
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  <HomeOutlined style={{ marginRight: 8 }} />
                  Parent/Guardian Information
                </span>
                {isSystemAdmin && localStudent?.parent && (
                  <Button
                    size="small"
                    icon={<i className="fas fa-edit" />}
                    onClick={() => openEdit("parent")}
                  >
                    Edit
                  </Button>
                )}
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            {localStudent?.parent ? (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Name">
                  <strong>{localStudent?.parent.fullName}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Relationship">
                  {localStudent?.parent.relationship || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  <MailOutlined style={{ marginRight: 8 }} />
                  {localStudent?.parent.email || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Phone (Primary)">
                  <PhoneOutlined style={{ marginRight: 8 }} />
                  {localStudent?.parent.phone || "-"}
                </Descriptions.Item>
                {localStudent?.parent.phoneAlt && (
                  <Descriptions.Item label="Phone (Alt)">
                    <PhoneOutlined style={{ marginRight: 8 }} />
                    {localStudent?.parent.phoneAlt}
                  </Descriptions.Item>
                )}
                <Descriptions.Item label="Address">
                  {localStudent?.parent.address || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Occupation">
                  {localStudent?.parent.occupation || "-"}
                </Descriptions.Item>
              </Descriptions>
            ) : localStudent?.parentAccount ? (
              // Fallback to linked parent account if no parent form data
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Name">
                  <strong>{localStudent?.parentAccount.fullName || "-"}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Email">
                  <MailOutlined style={{ marginRight: 8 }} />
                  {localStudent?.parentAccount.email || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  <PhoneOutlined style={{ marginRight: 8 }} />
                  {localStudent?.parentAccount.phone || "-"}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Alert
                message="No parent/guardian information available"
                type="info"
                showIcon
              />
            )}
          </Card>

          {/* Emergency Contact */}
          <Card
            size="small"
            title={
              <span
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <span>
                  <AlertOutlined style={{ marginRight: 8, color: "#ff4d4f" }} />
                  Emergency Contact
                </span>
                {isSystemAdmin && localStudent?.emergencyContact && (
                  <Button
                    size="small"
                    icon={<i className="fas fa-edit" />}
                    onClick={() => openEdit("emergency")}
                  >
                    Edit
                  </Button>
                )}
              </span>
            }
            style={{ marginBottom: 16 }}
          >
            {localStudent?.emergencySameAsParent ? (
              <Alert
                message="Same as Parent/Guardian"
                description="Emergency contact details are the same as parent/guardian."
                type="info"
                showIcon
              />
            ) : localStudent?.emergencyContact ? (
              <Descriptions column={1} size="small" bordered>
                <Descriptions.Item label="Name">
                  <strong>{localStudent?.emergencyContact.name}</strong>
                </Descriptions.Item>
                <Descriptions.Item label="Relationship">
                  {localStudent?.emergencyContact.relationship || "-"}
                </Descriptions.Item>
                <Descriptions.Item label="Phone">
                  <PhoneOutlined style={{ marginRight: 8 }} />
                  {localStudent?.emergencyContact.phone || "-"}
                </Descriptions.Item>
              </Descriptions>
            ) : (
              <Alert
                message="No emergency contact information available"
                type="warning"
                showIcon
              />
            )}
          </Card>

          {/* Transportation (for Day students) */}
          {localStudent?.dayBoarding === "Day" && (
            <Card
              size="small"
              title={
                <span>
                  <CarOutlined style={{ marginRight: 8 }} />
                  Transportation
                </span>
              }
              style={{ marginBottom: 16 }}
            >
              {hasTransport && latestTransport ? (
                <Descriptions column={2} size="small" bordered>
                  <Descriptions.Item label="Status">
                    <Tag color="green">Uses Transport</Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Type">
                    <Tag color="blue">
                      {latestTransport.transportType || "-"}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Route" span={2}>
                    {latestTransport.route || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="TPC">
                    {latestTransport.tpc || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Booking Status">
                    <Tag
                      color={
                        latestTransport.status === "Active"
                          ? "green"
                          : "default"
                      }
                    >
                      {latestTransport.status || "-"}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Academic Year">
                    {latestTransport.acadyr || "-"}
                  </Descriptions.Item>
                  <Descriptions.Item label="Term" span={2}>
                    {latestTransport.term || "-"}
                  </Descriptions.Item>
                </Descriptions>
              ) : (
                <Alert
                  message="No Transport Booking"
                  description="This student does not use school transportation."
                  type="info"
                  showIcon
                />
              )}
            </Card>
          )}

          {/* Financial Summary */}
          <Card
            size="small"
            title={`Financial Summary${
              localStudent?.financialSummary?.currentTerm?.name
                ? ` - ${localStudent.financialSummary.currentTerm.name}`
                : ""
            }`}
            style={{ marginBottom: 16 }}
          >
            {localStudent?.financialSummary ? (
              <>
                {/* Overall Status */}
                <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
                  <Col span={12}>
                    <Statistic
                      title="Tuition Status"
                      value={
                        localStudent?.financialSummary.tuitionCleared
                          ? "Cleared"
                          : "Pending"
                      }
                      valueStyle={{
                        color: localStudent?.financialSummary.tuitionCleared
                          ? "#52c41a"
                          : "#f5222d",
                      }}
                      prefix={
                        localStudent?.financialSummary.tuitionCleared ? (
                          <CheckCircleOutlined />
                        ) : (
                          <CloseCircleOutlined />
                        )
                      }
                    />
                  </Col>
                  <Col span={12}>
                    <Statistic
                      title="Overall Status"
                      value={
                        localStudent?.financialSummary.allCleared
                          ? "All Cleared"
                          : "Balance Due"
                      }
                      valueStyle={{
                        color: localStudent?.financialSummary.allCleared
                          ? "#52c41a"
                          : "#f5222d",
                      }}
                      prefix={
                        localStudent?.financialSummary.allCleared ? (
                          <CheckCircleOutlined />
                        ) : (
                          <CloseCircleOutlined />
                        )
                      }
                    />
                  </Col>
                </Row>

                <Divider style={{ margin: "12px 0" }} />

                {/* Fee Breakdown Table */}
                <table
                  style={{
                    width: "100%",
                    fontSize: "12px",
                    borderCollapse: "collapse",
                  }}
                >
                  <thead>
                    <tr
                      style={{ backgroundColor: "#fafafa", textAlign: "left" }}
                    >
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Fee Type
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        Due
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        Paid
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        Balance
                      </th>
                      <th
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "center",
                        }}
                      >
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {/* Current-term tuition */}
                    <tr>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Current Term Tuition
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.tuitionFees
                            ?.totalDue || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.tuitionFees
                            ?.totalPaid || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.tuitionFees?.balance ||
                            0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "center",
                        }}
                      >
                        <Tag
                          color={
                            localStudent?.financialSummary.tuitionFees?.cleared
                              ? "green"
                              : "red"
                          }
                          style={{ margin: 0 }}
                        >
                          {localStudent?.financialSummary.tuitionFees?.cleared
                            ? "Cleared"
                            : "Pending"}
                        </Tag>
                      </td>
                    </tr>

                    {/* Custom Fees */}
                    <tr>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Custom Fees
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.customFees?.totalDue ||
                            0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.customFees
                            ?.totalPaid || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.customFees?.balance ||
                            0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "center",
                        }}
                      >
                        <Tag
                          color={
                            localStudent?.financialSummary.customFees?.cleared
                              ? "green"
                              : "red"
                          }
                          style={{ margin: 0 }}
                        >
                          {localStudent?.financialSummary.customFees?.cleared
                            ? "Cleared"
                            : "Pending"}
                        </Tag>
                      </td>
                    </tr>

                    {/* BOT Fees */}
                    <tr>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        BOT Fees
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.botFees?.totalDue || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.botFees?.totalPaid ||
                            0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.botFees?.balance || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "center",
                        }}
                      >
                        <Tag
                          color={
                            localStudent?.financialSummary.botFees?.cleared
                              ? "green"
                              : "red"
                          }
                          style={{ margin: 0 }}
                        >
                          {localStudent?.financialSummary.botFees?.cleared
                            ? "Cleared"
                            : "Pending"}
                        </Tag>
                      </td>
                    </tr>

                    {/* Transport Fees */}
                    <tr>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Transport Fees
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.transportFees
                            ?.totalDue || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.transportFees
                            ?.totalPaid || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                          fontWeight: 500,
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.transportFees
                            ?.balance || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "center",
                        }}
                      >
                        <Tag
                          color={
                            localStudent?.financialSummary.transportFees
                              ?.cleared
                              ? "green"
                              : "red"
                          }
                          style={{ margin: 0 }}
                        >
                          {localStudent?.financialSummary.transportFees?.cleared
                            ? "Cleared"
                            : "Pending"}
                        </Tag>
                      </td>
                    </tr>

                    {/* Current-term total */}
                    <tr style={{ backgroundColor: "#fafafa", fontWeight: 600 }}>
                      <td style={{ padding: "8px" }}>Current Term Total</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>
                        {formatCurrency(
                          localStudent?.financialSummary.grandTotal?.totalDue ||
                            0,
                        )}
                      </td>
                      <td style={{ padding: "8px", textAlign: "right" }}>
                        {formatCurrency(
                          localStudent?.financialSummary.grandTotal
                            ?.totalPaid || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          color:
                            parseFloat(
                              localStudent?.financialSummary.grandTotal
                                ?.balance || 0,
                            ) > 0
                              ? "#f5222d"
                              : "#52c41a",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.grandTotal?.balance ||
                            0,
                        )}
                      </td>
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        <Tag
                          color={
                            localStudent?.financialSummary.allCleared
                              ? "green"
                              : "red"
                          }
                          style={{ margin: 0 }}
                        >
                          {localStudent?.financialSummary.allCleared
                            ? "Cleared"
                            : "Pending"}
                        </Tag>
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Balance Brought Forward
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        -
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        -
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                          color: "#fa8c16",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary
                            .balanceBroughtForward || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "center",
                        }}
                      >
                        <Tag color="orange" style={{ margin: 0 }}>
                          Prior Term
                        </Tag>
                      </td>
                    </tr>
                    <tr>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                        }}
                      >
                        Prepayment Credit
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        -
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                        }}
                      >
                        -
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "right",
                          color: "#52c41a",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.prepaymentCredit || 0,
                        )}
                      </td>
                      <td
                        style={{
                          padding: "8px",
                          borderBottom: "1px solid #f0f0f0",
                          textAlign: "center",
                        }}
                      >
                        <Tag color="green" style={{ margin: 0 }}>
                          Credit
                        </Tag>
                      </td>
                    </tr>
                    <tr style={{ fontWeight: 600 }}>
                      <td style={{ padding: "8px" }}>Net Outstanding</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>-</td>
                      <td style={{ padding: "8px", textAlign: "right" }}>-</td>
                      <td
                        style={{
                          padding: "8px",
                          textAlign: "right",
                          color: "#f5222d",
                        }}
                      >
                        {formatCurrency(
                          localStudent?.financialSummary.netOutstanding || 0,
                        )}
                      </td>
                      <td style={{ padding: "8px", textAlign: "center" }}>
                        <Tag color="red" style={{ margin: 0 }}>
                          Net Position
                        </Tag>
                      </td>
                    </tr>
                  </tbody>
                </table>
              </>
            ) : (
              <Alert
                message="No financial data available"
                type="info"
                showIcon
              />
            )}
          </Card>

          <Modal
            title={
              editSection === "student"
                ? "Edit Personal Information"
                : editSection === "academic"
                  ? "Edit Academic Context"
                  : editSection === "parent"
                    ? "Edit Parent/Guardian"
                    : "Edit Emergency Contact"
            }
            open={!!editSection}
            onCancel={() => {
              if (!editLoading && !academicSaving.current) setEditSection(null);
            }}
            onOk={saveEdit}
            confirmLoading={editLoading}
            cancelButtonProps={{ disabled: editLoading }}
            closable={!editLoading}
            maskClosable={!editLoading}
            keyboard={!editLoading}
            okButtonProps={{
              disabled:
                editSection === "academic" &&
                (contextOptionsLoading || !!contextOptionsError),
            }}
            okText="Save"
          >
            <Form form={editForm} layout="vertical">
              {editSection === "student" && (
                <>
                  <Form.Item name="firstName" label="First Name">
                    <Input />
                  </Form.Item>
                  <Form.Item name="lastName" label="Last Name">
                    <Input />
                  </Form.Item>
                  <Form.Item name="middleName" label="Middle Name">
                    <Input />
                  </Form.Item>
                  <Form.Item name="dob" label="Date of Birth">
                    <DatePicker style={{ width: "100%" }} />
                  </Form.Item>
                  <Form.Item name="gender" label="Gender">
                    <Select>
                      <Select.Option value="Male">Male</Select.Option>
                      <Select.Option value="Female">Female</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item name="nationality" label="Nationality">
                    <Select showSearch options={nationalityOptions} />
                  </Form.Item>
                  <Form.Item
                    name="linField"
                    label="Learner Identification Number (LIN)"
                    rules={[
                      {
                        max: 30,
                        message: "LIN must be 30 characters or fewer",
                      },
                    ]}
                  >
                    <Input maxLength={30} />
                  </Form.Item>
                </>
              )}
              {editSection === "academic" && (
                <>
                  {academicError && (
                    <Alert
                      type="error"
                      showIcon
                      message={academicError}
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {contextOptionsError && (
                    <Alert
                      type="error"
                      showIcon
                      message={contextOptionsError}
                      action={
                        <Button
                          onClick={() =>
                            setContextOptionsVersion((version) => version + 1)
                          }
                        >
                          Retry loading
                        </Button>
                      }
                      style={{ marginBottom: 16 }}
                    />
                  )}
                  {editDayBoarding === "Day" &&
                    localStudent?.dayBoarding === "Boarding" && (
                      <Alert
                        type="info"
                        showIcon
                        message="Switching to Day removes the Dormitory assignment and keeps the House assignment. Eligible fees and transport bookings will also be updated."
                        style={{ marginBottom: 16 }}
                      />
                    )}
                  <Form.Item
                    name="dayBoarding"
                    label="Day/Boarding"
                    rules={[
                      { required: true, message: "Select Day or Boarding" },
                    ]}
                  >
                    <Select disabled={editLoading || contextOptionsLoading}>
                      <Select.Option value="Day">Day</Select.Option>
                      <Select.Option value="Boarding">Boarding</Select.Option>
                    </Select>
                  </Form.Item>
                  <Form.Item
                    name="currentTermId"
                    label="Current Term"
                    rules={[{ required: true, message: "Select current term" }]}
                  >
                    <Select
                      placeholder="Select term"
                      loading={contextOptionsLoading}
                      disabled={editLoading || contextOptionsLoading}
                    >
                      {terms.map((term) => (
                        <Select.Option key={term.id} value={term.id}>
                          {term.name}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                  <Form.Item name="houseID" label="House">
                    <Select
                      placeholder="Select house"
                      showSearch
                      optionFilterProp="label"
                      loading={contextOptionsLoading}
                      disabled={
                        editLoading ||
                        contextOptionsLoading ||
                        !!contextOptionsError
                      }
                      options={accommodations
                        .filter((item) => item.category === "House")
                        .map((item) => ({
                          value: item.id,
                          label: item.name,
                        }))}
                    />
                  </Form.Item>
                  {editDayBoarding === "Boarding" && (
                    <Form.Item name="dormitoryID" label="Dormitory">
                      <Select
                        placeholder="Select dormitory"
                        showSearch
                        optionFilterProp="label"
                        loading={contextOptionsLoading}
                        disabled={
                          editLoading ||
                          contextOptionsLoading ||
                          !!contextOptionsError
                        }
                        options={accommodations
                          .filter((item) => item.category === "Dormitory")
                          .map((item) => ({
                            value: item.id,
                            label: `${item.name} (${item.remainingSpace} spaces remaining)`,
                            disabled:
                              Number(item.remainingSpace) <= 0 &&
                              String(item.id) !==
                                String(localStudent?.boarding?.dormitory?.id),
                          }))}
                      />
                    </Form.Item>
                  )}
                </>
              )}
              {editSection === "parent" && (
                <>
                  <Form.Item name="parentName" label="Full Name">
                    <Input />
                  </Form.Item>
                  <Form.Item
                    name="relationshipId"
                    label="Relationship to Student"
                  >
                    <Select
                      showSearch
                      options={relationshipOptions}
                      placeholder="Relationship"
                    />
                  </Form.Item>
                  <Form.Item name="email" label="Email">
                    <Input />
                  </Form.Item>
                  <Form.Item name="phonePrimary" label="Phone (Primary)">
                    <Input
                      addonBefore={CustomFunctions.prefixSelector(
                        "countryCodePri",
                      )}
                    />
                  </Form.Item>
                  <Form.Item name="phoneAlt" label="Phone (Alt)">
                    <Input
                      addonBefore={CustomFunctions.prefixSelector(
                        "countryCodeAlt",
                      )}
                    />
                  </Form.Item>
                  <Form.Item name="address" label="Address">
                    <Input />
                  </Form.Item>
                  <Form.Item name="occupation" label="Occupation">
                    <Input />
                  </Form.Item>
                </>
              )}
              {editSection === "emergency" && (
                <>
                  <Form.Item name="emgName" label="Name">
                    <Input />
                  </Form.Item>
                  <Form.Item name="emgRelId" label="Relationship">
                    <Select
                      showSearch
                      options={relationshipOptions}
                      placeholder="Relationship"
                    />
                  </Form.Item>
                  <Form.Item name="emgPhone" label="Phone">
                    <Input
                      addonBefore={CustomFunctions.prefixSelector(
                        "countryCodeEmg",
                      )}
                    />
                  </Form.Item>
                </>
              )}
            </Form>
          </Modal>
        </>
      ) : (
        <Alert message="No student selected" type="info" showIcon />
      )}
    </Drawer>
  );
};

export default StudentDetailDrawer;
